import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { euro } from "@/lib/api";
import { toast } from "sonner";
import { X, Locate } from "lucide-react";
import { CITY_COORDS, plzToCoords, dist } from "@/lib/geo";

function pinIcon(count) {
  return L.divIcon({
    className: "",
    html: `<div style="background:#059669;color:#fff;width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff"><span style="transform:rotate(45deg);font-weight:700;font-size:13px">${count}</span></div>`,
    iconSize: [34, 34], iconAnchor: [17, 34], popupAnchor: [0, -34],
  });
}
function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, zoom); }, [center, zoom, map]);
  return null;
}

export const OffersMap = ({ services = [] }) => {
  const navigate = useNavigate();
  const [plz, setPlz] = useState("");
  const [radius, setRadius] = useState(50);
  const [center, setCenter] = useState(null);

  const groups = useMemo(() => {
    const g = {};
    services.forEach((s) => {
      const c = CITY_COORDS[s.city];
      if (!c) return;
      if (center && dist(center, c) > radius) return;
      if (!g[s.city]) g[s.city] = { coords: c, items: [] };
      g[s.city].items.push(s);
    });
    return g;
  }, [services, center, radius]);

  const apply = () => {
    const c = plzToCoords(plz.trim());
    if (!c) { toast.error("PLZ nicht erkannt – bitte 5-stellige PLZ eingeben."); return; }
    setCenter(c);
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Standort wird von deinem Browser nicht unterstützt."); return; }
    toast.info("Standort wird ermittelt…");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setPlz(""); setCenter([pos.coords.latitude, pos.coords.longitude]); toast.success("Standort erkannt – Angebote in deiner Nähe."); },
      () => toast.error("Standort konnte nicht ermittelt werden – bitte Berechtigung erlauben."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  const reset = () => { setPlz(""); setCenter(null); };
  const total = Object.values(groups).reduce((n, g) => n + g.items.length, 0);
  const zoom = radius >= 150 ? 6 : radius >= 75 ? 7 : 8;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="radius-filter">
        <Input data-testid="plz-input" value={plz} inputMode="numeric" onChange={(e) => setPlz(e.target.value.replace(/\D/g, "").slice(0, 5))} onKeyDown={(e) => e.key === "Enter" && apply()}
          placeholder="PLZ, z.B. 80331" className="w-40" />
        <select data-testid="radius-select" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value={25}>25 km</option>
          <option value={50}>50 km</option>
          <option value={100}>100 km</option>
          <option value={200}>200 km</option>
        </select>
        <Button onClick={apply} className="bg-emerald-600 hover:bg-emerald-700" data-testid="apply-radius">Umkreis anzeigen</Button>
        <Button variant="outline" onClick={useMyLocation} className="gap-1" data-testid="use-location"><Locate className="w-4 h-4" /> In meiner Nähe</Button>
        {center && (
          <Button variant="outline" onClick={reset} className="gap-1" data-testid="reset-radius"><X className="w-4 h-4" /> Zurücksetzen</Button>
        )}
        {center && <span className="text-sm text-slate-500" data-testid="radius-count">{total} Angebote im Umkreis von {radius} km</span>}
      </div>
      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" style={{ height: "560px" }} data-testid="map-container">
        <MapContainer center={[51.16, 10.45]} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Recenter center={center} zoom={zoom} />
          {center && <Circle center={center} radius={radius * 1000} pathOptions={{ color: "#059669", fillColor: "#10B981", fillOpacity: 0.1 }} />}
          {Object.entries(groups).map(([city, g]) => (
            <Marker key={city} position={g.coords} icon={pinIcon(g.items.length)}>
              <Popup>
                <div className="font-bold mb-1 text-slate-900">{city}</div>
                {g.items.map((s) => (
                  <div key={s.id} className="py-1 border-b border-slate-100 last:border-0 cursor-pointer" onClick={() => navigate(`/angebot/${s.id}`)}>
                    <div className="font-semibold text-emerald-700">{s.title}</div>
                    <div className="text-xs text-slate-500">{euro(s.base_price)} · {s.base_persons} Pers.</div>
                  </div>
                ))}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};
